import React, {useCallback, useEffect, useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {Button, DialogActions, DialogContent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Call from "../../hocs/call";
import ConfirmationDialog from "../confirmation-dialog";
import CustomJsonForms from "../custom-json-forms";
import FullscreenDialog from "../fullscreen-dialog";
import MaterialTable from "../material-table";
import SettingsDialog from "../settings-dialog";
import {appSelector} from "../../state/app/appSelectors";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {
  createResourceLicense,
  deleteResourceLicense,
  fetchResourceLicenseList,
  fetchResourceLicenseSchema,
  getResourceLicense,
  hideResourceLicense,
  hideResourceLicenseList,
  showResourceLicense,
  updateResourceLicense
} from "../../state/package-manager/packageManagerActions";
import {resourceLicensesManagerSelector} from "../../state/package-manager/packageManagerSelectors";
import {localizeI18nObj} from "../../utils/i18n";

const ResourceLicensesManager = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {language, languages} = useSelector(appSelector);

  const nodeId = useSelector(nodeIdSelector);
  const {isVisible, resourceLicenses, schema, uiSchema, resourceLicenseId, resourceLicense} = useSelector(
    resourceLicensesManagerSelector
  );

  const [resourceLicenseToDelete, setResourceLicenseToDelete] = useState(null);

  const [data, setData] = useState(null);
  const [errors, setErrors] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  const handleFetchResourceLicenses = useCallback(() => {
    dispatch(fetchResourceLicenseList(nodeId));
  }, [dispatch, nodeId]);

  const handleCloseResourceLicensesDialog = useCallback(() => {
    dispatch(hideResourceLicenseList());
  }, [dispatch]);

  const handleCloseResourceLicenseDialog = useCallback(() => {
    dispatch(hideResourceLicense());
  }, [dispatch]);

  const handleCreateResourceLicense = useCallback(() => {
    dispatch(showResourceLicense());
  }, [dispatch]);

  const handleEditResourceLicense = useCallback(
    (_, {__Guid, title}) => {
      dispatch(getResourceLicense(nodeId, __Guid, title));
    },
    [dispatch, nodeId]
  );

  const handleFetchResourceLicenseSchema = useCallback(() => {
    dispatch(fetchResourceLicenseSchema(nodeId));
  }, [dispatch, nodeId]);

  const handleSubmitResourceLicense = useCallback(() => {
    if ((errors || []).length > 0) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
      if (resourceLicenseId) {
        dispatch(updateResourceLicense(nodeId, resourceLicenseId, data));
      } else {
        dispatch(createResourceLicense(nodeId, data));
      }
    }
  }, [dispatch, errors, resourceLicenseId, nodeId, data]);

  const handleStartDelete = useCallback((_, resourceLicense) => {
    setResourceLicenseToDelete(resourceLicense);
  }, []);

  const handleAbortDelete = useCallback(() => {
    setResourceLicenseToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    dispatch(deleteResourceLicense(nodeId, resourceLicenseToDelete.__Guid, resourceLicenseToDelete.title));
    setResourceLicenseToDelete(null);
  }, [dispatch, nodeId, resourceLicenseToDelete]);

  useEffect(() => {
    setData(resourceLicense);
    setErrors(null);
    setShowValidation(false);

    return () => {
      setData(null);
      setErrors(null);
      setShowValidation(false);
    };
  }, [resourceLicense]);

  if (!nodeId || !isVisible) {
    return null;
  }

  return (
    <SettingsDialog
      title={t("modules.packageManager.resourceLicensesManager.dialog.title")}
      open={isVisible}
      onClose={handleCloseResourceLicensesDialog}
      className="resource-licenses-manager__resource-licenses-dialog"
    >
      <Call cb={handleFetchResourceLicenses} disabled={resourceLicenses !== null}>
        <MaterialTable
          id={"resource-licenses-manager__table"}
          data={resourceLicenses || []}
          rightActions={[
            {
              id: "resource-licenses-manager__table__actions__create",
              label: t("modules.packageManager.resourceLicensesManager.actions.create.label"),
              startIcon: <AddIcon />,
              onClick: handleCreateResourceLicense
            }
          ]}
          actions={[
            {
              icon: <EditIcon />,
              tooltip: t("modules.packageManager.resourceLicensesManager.actions.edit.tooltip"),
              onClick: handleEditResourceLicense,
              className: "resource-licenses-manager__table__actions__edit"
            },
            {
              icon: <DeleteIcon />,
              tooltip: t("modules.packageManager.resourceLicensesManager.actions.delete.tooltip"),
              onClick: handleStartDelete,
              className: "resource-licenses-manager__table__actions__delete"
            }
          ]}
          columns={[
            {
              field: "__Guid",
              title: t("modules.packageManager.resourceLicensesManager.columns.id.label"),
              hidden: true,
              getClassName: () => "resource-licenses-manager__table__columns__id"
            },
            {
              field: "title",
              title: t("modules.packageManager.resourceLicensesManager.columns.title.label"),
              render: ({title}) => localizeI18nObj(title, language, languages),
              getClassName: () => "resource-licenses-manager__table__columns__id"
            },
            {
              field: "parent",
              title: t("modules.packageManager.resourceLicensesManager.columns.parent.label"),
              render: ({parent}) => {
                const resourceLicenseParent = resourceLicenses?.find(pl => pl.__Guid === parent);
                return localizeI18nObj(resourceLicenseParent?.title, language, languages);
              },
              getClassName: () => "resource-licenses-manager__table__columns__parent"
            }
          ]}
        />

        <ConfirmationDialog
          title={t("modules.packageManager.resourceLicensesManager.deleteDialog.title")}
          content={t("modules.packageManager.resourceLicensesManager.deleteDialog.description")}
          open={resourceLicenseToDelete !== null}
          onClose={handleAbortDelete}
          onConfirm={handleConfirmDelete}
          className="resource-licenses-manager__delete-resource-license-dialog"
        />

        <FullscreenDialog
          open={resourceLicense !== null}
          title={
            resourceLicenseId
              ? t("modules.packageManager.resourceLicensesManager.editDialog.title")
              : t("modules.packageManager.resourceLicensesManager.createDialog.title")
          }
          onClose={handleCloseResourceLicenseDialog}
          className="resource-licenses-manager__resource-license-dialog"
        >
          <DialogContent>
            <Call cb={handleFetchResourceLicenseSchema} disabled={schema !== null}>
              {schema && uiSchema && (
                <CustomJsonForms
                  schema={schema}
                  uischema={uiSchema}
                  data={data}
                  setData={setData}
                  errors={errors}
                  setErrors={setErrors}
                  showValidation={showValidation}
                  i18nPrefix={"modules.packageManager.resourceLicensesManager"}
                />
              )}
            </Call>
          </DialogContent>
          <DialogActions>
            <Button
              className="resource-licenses-manager__resource-license-dialog__cancel-btn"
              onClick={handleCloseResourceLicenseDialog}
            >
              {t("commons.confirm.cancel")}
            </Button>
            <Button
              className="resource-licenses-manager__resource-license-dialog__submit-btn"
              onClick={handleSubmitResourceLicense}
            >
              {t("commons.confirm.submit")}
            </Button>
          </DialogActions>
        </FullscreenDialog>
      </Call>
    </SettingsDialog>
  );
};

export default ResourceLicensesManager;
